import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CommandMenuProvider } from "@/components/command-menu";
import { AppLayout } from "@/components/layout";
import { HomePage } from "@/pages/home";
import { DemoPage } from "@/pages/demo";
import { SignInPage } from "@/pages/sign-in";
import { SignUpPage } from "@/pages/sign-up";
import { PendingPage } from "@/pages/pending";
import { PlaygroundPage } from "@/pages/playground";
import { HistoryPage } from "@/pages/history";
import { GodDashboardPage } from "@/pages/god-dashboard";
import { AdminProjectsPage } from "@/pages/admin/projects";
import { AdminUsersPage } from "@/pages/admin/users";
import { AdminRunnersPage } from "@/pages/admin/runners";
import { AdminApiKeysPage } from "@/pages/admin/api-keys";

export function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function Gate() {
  const { status } = useAuth();

  if (status === "loading") {
    return <FullPageStatus message="Loading…" />;
  }
  if (status === "signed-out") {
    return (
      <Routes>
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="*" element={<SignInPage />} />
      </Routes>
    );
  }
  if (status === "pending") {
    return <PendingPage />;
  }

  return (
    <CommandMenuProvider>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<LandingRoute />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/playground" element={<PlaygroundPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/god" element={<GodDashboardPage />} />
          <Route path="/admin/projects" element={<AdminProjectsPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/runners" element={<AdminRunnersPage />} />
          <Route path="/admin/api-keys" element={<AdminApiKeysPage />} />
        </Route>
      </Routes>
    </CommandMenuProvider>
  );
}

function LandingRoute() {
  const { me } = useAuth();
  if (me && isDemoUser(me)) return <Navigate to="/demo" replace />;
  return <HomePage />;
}

function isDemoUser(me: NonNullable<ReturnType<typeof useAuth>["me"]>) {
  return (
    me.roles.length > 0 &&
    me.roles.every((role) => role.role === "viewer")
  );
}

function FullPageStatus({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center text-muted-foreground">
      {message}
    </div>
  );
}
